import {
    LoaderCircle,
    RefreshCw,
    FileText,
    Tag,
    DollarSign,
    TrendingUp,
    Users,
    UserPlus,
    Codepen,
    ChevronRight,
    ExternalLink
  } from 'lucide-react';
  import React, { useEffect, useState } from 'react';
  import { Link } from 'react-router-dom';
  import { projectAPI } from '../../services/api';
  import { motion } from 'framer-motion';
  import skyLoader from "../../assets/home-assets/skyLoader.gif";
  const FeaturedProjects = () => {
    const [projects, setProjects] = useState([]);
    const [filteredProjects, setFilteredProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
  
    useEffect(() => {
      fetchProjects();
    }, []);
  
    const fetchProjects = async () => {
      try {
        setLoading(true);
        const response = await projectAPI.getAllPublic();
        setProjects(response.data);
        setFilteredProjects(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching projects:', error);
        setError(error.message);
        setLoading(false);
      }
    };
  
    const handleRefresh = () => {
      fetchProjects();
    };
    
    if (loading)
      return (
        <div className="flex justify-center items-center min-h-screen ">
            <img 
              src={skyLoader} 
              alt="Loading..." 
              className="w-100 h-100 object-contain"
            />
          </div>
      );
  
    if (error)
      return (
        <div className="text-red-500 p-4 border border-red-200 rounded-lg bg-red-50">
          <p>Error: {error}</p>
          <button
            onClick={handleRefresh}
            className="mt-2 flex items-center text-blue-600 hover:text-blue-800"
          >
            <RefreshCw size={16} className="mr-1" />
            Try again
          </button>
        </div>
      );
  
    if (filteredProjects.length === 0)
      return (
        <div className="text-center p-8">
          <h3 className="text-lg font-medium text-gray-700">No projects found</h3>
          <p className="text-gray-500 mt-2">
            Be the first to create a project and start collaborating!
          </p>
        </div>
      );
  
    const cardVariants = {
      hidden: { opacity: 0, y: 20 },
      visible: { 
        opacity: 1, 
        y: 0,
        transition: { duration: 0.5 }
      }
    };

    return (
      <div className="px-4">
        {/* Header with Title and View All Link */}
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 leading-tight">
            Featured Projects
          </h2>
          <Link
            to="/projects"
            className="flex items-center text-indigo-600 hover:text-indigo-800 transition-colors text-sm font-medium"
          >
            View All
            <ChevronRight size={16} className="ml-1" />
          </Link>
        </div>
  
        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProjects?.slice(0, 6)?.map((project, index) => (
            <motion.div
              key={project.projectTokenId}
              className="bg-white rounded-xl overflow-hidden shadow-md border border-gray-200 hover:shadow-lg hover:border-indigo-200 transition-all duration-300"
              variants={cardVariants}
              initial="hidden"
              animate="visible"
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -5, transition: { duration: 0.2 } }}
            >
              {/* Project Header */}
              <div className="p-5 border-b border-gray-100">
                <div className="flex items-start justify-between">
                  <div className="flex items-center">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-md mr-3">
                      <Codepen size={20} className="text-white" />
                    </div>
                    <div>
                      <Link 
                        to={`/projects/token/${project.projectTokenId}`}
                        className="text-xl font-bold text-gray-800 hover:text-indigo-600 transition-colors"
                      >
                        {project.title}
                      </Link>
                      <div className="flex items-center mt-1">
                        <Tag size={14} className="text-gray-500 mr-1" />
                        <span className="text-gray-600 text-sm">
                          {project.primaryDomain || 'General'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <Link
                    to={`/projects/token/${project.projectTokenId}`}
                    className="rounded-full p-2 hover:bg-indigo-50 transition-colors"
                  >
                    <ExternalLink size={16} className="text-indigo-500" />
                  </Link>
                </div>
                <p className="text-gray-600 mt-3 text-sm line-clamp-2">
                  {project.description || "Build something amazing with our community of innovators."}
                </p>
              </div>

              {/* Project Stats */}
              <div className="grid grid-cols-2 gap-4 p-5">
                <div className="flex flex-col">
                  <div className="flex items-center text-gray-500 text-xs mb-1">
                    <DollarSign size={14} className="mr-1" />
                    <span>Investment</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-indigo-600 font-bold">{project.totalInvestment || 0}</span>
                    <span className="text-indigo-500 ml-1 text-sm">CEL</span>
                  </div>
                </div>

                <div className="flex flex-col">
                  <div className="flex items-center text-gray-500 text-xs mb-1">
                    <TrendingUp size={14} className="mr-1" />
                    <span>Staked</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-blue-600 font-bold">{project.totalStakedAmount || 0}</span>
                    <span className="text-blue-500 ml-1 text-sm">CEL</span>
                  </div>
                </div>

                <div className="flex flex-col">
                  <div className="flex items-center text-gray-500 text-xs mb-1">
                    <Users size={14} className="mr-1" />
                    <span>Investors</span>
                  </div>
                  <span className="text-purple-600 font-bold">{project.totalInvestors || 0}</span>
                </div>

                <div className="flex flex-col">
                  <div className="flex items-center text-gray-500 text-xs mb-1">
                    <UserPlus size={14} className="mr-1" />
                    <span>Contributors</span>
                  </div>
                  <span className="text-pink-600 font-bold">{project.contributors?.length || 0}</span>
                </div>
              </div>

              {/* Project Footer */}
              <div className="px-5 py-3 bg-gray-50 border-t border-gray-100">
                <Link
                  to={`/projects/token/${project.projectTokenId}`}
                  className="w-full block text-center text-sm font-medium text-indigo-600 hover:text-indigo-800 transition-colors"
                >
                  View Details
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    );
  };
  
  export default FeaturedProjects;
  